/**
 * Single-owner lock around the `social-login-swap` one-time-token mint.
 *
 * WHY THIS EXISTS (PostHog issue 019fd27e, 2026-08-05, native /login, google)
 * --------------------------------------------------------------------------
 * `verifyOtp` answered "Email link is invalid or has expired" for a token that
 * `social-login-swap` had minted seconds earlier, on attempt 0. Two things were
 * checked first and BOTH are correct, so neither explains it:
 *
 *   - the `type` matches. The server calls
 *     `admin.generateLink({ type: 'magiclink' })` and every client redemption
 *     passes `type: 'magiclink'`. There is no mismatch to fix.
 *   - staleness does not fit. The mint and the redemption are the same request
 *     cycle, well inside any plausible OTP expiry.
 *
 * What remains is INVALIDATION, and there is exactly one mechanism for it in
 * this codebase. GoTrue's `generateLink` does not issue an independent token —
 * it (re)writes the one-time token stored on the target auth user, so a second
 * mint for the same account silently voids the first. Redeeming the first then
 * fails with precisely this message.
 *
 * And there are TWO independent swap implementations that both invoke
 * `social-login-swap`:
 *
 *   1. `finalizeOAuth.swapToLinkedCnicAccount()`  — the owner
 *   2. `useAuthState.maybeSwapSocialSession()`    — the legacy path
 *
 * (2) skips itself only while an OAuth intent is in flight, and that intent
 * carries a 10-minute TTL (`oauthIntent.ts`). Any return that outlives the TTL
 * — a slow native Chrome Custom Tab round-trip, an account picker left open, a
 * 2FA prompt — drops the guard and lets both paths mint against the same
 * account. Whichever redeems second gets an already-overwritten token.
 *
 * This lock makes the mint single-owner regardless of intent state. It is
 * deliberately NOT a refactor of either auth path: CLAUDE.md §7 locks
 * CNIC/email/phone login against re-litigation, so the fix is the smallest
 * thing that removes the hazard.
 *
 * The cooldown is CROSS-OWNER ONLY. The holder may re-acquire freely (the
 * finalizer's own retry loop must not be starved); a different caller is held
 * off just long enough for a minted token to be redeemed.
 */

/** Redemption window a fresh token needs before another caller may re-mint. */
const CROSS_OWNER_COOLDOWN_MS = 10_000;

/** Hard ceiling — a holder that never releases (crash mid-swap) must not wedge
 *  the swap for the rest of the session. Comfortably longer than the swap's own
 *  retry budget so it can never cut a legitimate attempt short. */
const HOLDER_STALE_MS = 60_000;

let holder: string | null = null;
let holderSince = 0;
let lastReleasedBy: string | null = null;
let lastReleasedAt = 0;

/**
 * Try to become the sole minter of a swap token.
 *
 * Returns false when another caller is mid-mint, or released one so recently
 * that its token is probably still being redeemed. A false answer means
 * "someone else owns this swap" — the caller should fall through to its normal
 * non-swap path, never treat it as a failure.
 */
export function tryAcquireSwapMint(owner: string): boolean {
  const now = Date.now();

  if (holder !== null) {
    if (holder === owner) {
      holderSince = now;
      return true;
    }
    if (now - holderSince < HOLDER_STALE_MS) return false;
    // Previous holder never released — reclaim rather than wedge forever.
    console.warn("[swap-mint] reclaiming stale lock", { staleHolder: holder, owner });
  } else if (
    lastReleasedBy !== null &&
    lastReleasedBy !== owner &&
    now - lastReleasedAt < CROSS_OWNER_COOLDOWN_MS
  ) {
    return false;
  }

  holder = owner;
  holderSince = now;
  return true;
}

/** Release the lock. Safe to call unconditionally; a non-holder is ignored. */
export function releaseSwapMint(owner: string): void {
  if (holder !== owner) return;
  holder = null;
  lastReleasedBy = owner;
  lastReleasedAt = Date.now();
}
